import { FC, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { CopyOutlined } from '@ant-design/icons';
import cn from 'classnames';

import { useNotices } from '@/features/notices/useNotices';
import { getAddressDisplay } from '@/shared/helpers/addresses';
import { Address } from '@/shared/lib/forms/form-wrapper.types';
import { CountryCode } from '@/shared/types';
import { Input } from '@/shared/uikitV2/Input';
import { copy } from '@/shared/utils/misc';
import { useModalsStore } from '@/widgets/modals/modals.store';

type AddressFieldProps = {
  label: string;
  name: string;
  required?: boolean;
  isHighlight?: boolean;
};

export const AddressField: FC<AddressFieldProps> = ({ label, name, required, isHighlight }) => {
  const { t } = useTranslation(['forms_assets']);
  const modals = useModalsStore();
  const notices = useNotices();
  const { control, setValue } = useFormContext();

  const assetCountry = useWatch({ control, name: 'country' }) as CountryCode | undefined;
  const addressValue = useWatch({ control, name }) as Address | undefined;

  useEffect(() => {
    if (assetCountry && addressValue) {
      const updatedValue: Address = {
        ...addressValue,
        country: assetCountry,
      };

      setValue(name, updatedValue, { shouldValidate: true });
    }
  }, [assetCountry, setValue]);

  const getRequiredRule = () => {
    if (!required) return undefined;
    return t('forms_assets:common.inputs.required');
  };

  const handleCopy = async (value: Address | undefined) => {
    if (!value) return;
    const addressText = getAddressDisplay(value);
    try {
      await copy(addressText);
      notices.success('Address copied to clipboard');
    } catch (e) {
      notices.error('Failed to copy address');
    }
  };

  const handleOpenModal = (
    currentValue: Address | undefined,
    onChange: (value: Address | undefined) => void,
  ) => {
    let addressData: Address | undefined = currentValue;

    if (assetCountry && (!addressData || !addressData.country)) {
      addressData = {
        ...addressData,
        country: assetCountry,
        zipCode: addressData?.zipCode || '',
        district: addressData?.district || '',
        city: addressData?.city || '',
        street: addressData?.street || '',
        buildingNumber: addressData?.buildingNumber || '',
        apartment: addressData?.apartment || '',
      };
    }

    modals.openModal({
      type: 'EditAddress',
      address: addressData,
      countryReadonly: assetCountry !== undefined,
      onConfirm: (newAddress: Address) => {
        onChange(newAddress);

        if (setValue && newAddress.country && assetCountry === undefined) {
          setValue('country', newAddress.country, { shouldValidate: true });
        }
      },
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: getRequiredRule() }}
      render={({ field, fieldState }) => (
        <Input
          className={cn({ highlight: isHighlight && !fieldState.isDirty })}
          label={label}
          errorText={fieldState.error?.message}
          value={getAddressDisplay(field.value)}
          readOnly
          onClick={() => handleOpenModal(field.value, field.onChange)}
          iconAfter={
            field.value ? (
              <CopyOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(field.value);
                }}
                style={{ cursor: 'pointer' }}
              />
            ) : undefined
          }
        />
      )}
    />
  );
};
